import {Dialog} from '@angular/cdk/dialog';
import {CommonModule} from '@angular/common';
import {
    AfterViewInit,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    HostListener,
    Inject,
    inject,
    OnDestroy,
    OnInit,
    signal,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {NavigationEnd, Router, RouterLink} from '@angular/router';
import {Collapse} from 'bootstrap';
import {combineLatest, Observable, of, Subject} from 'rxjs';
import {catchError, filter, finalize, first, map, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';

import {NavigationListComponent} from 'apps/faculty/src/app/components/navigation-list/navigation-list.component';
import {FacultyAppContextService} from 'libs/shared/faculty/src/lib/service/faculty-app-context.service';
import {FacultyAccountModel} from 'libs/data-access/faculty/src/lib/models/faculty.model';
import {FacultyService} from 'libs/data-access/faculty/src/lib/services/faculty.service';
import {InstitutionInformationModel} from 'libs/data-access/institution/src/lib/models/institution-information.model';
import {LearnerText} from 'libs/data-access/institution/src/lib/models/learner.model';
import {InstitutionService} from 'libs/data-access/institution/src/lib/services/institution.service';
import {InstitutionContextService} from 'libs/data-access/institution/src/lib/services/institution-context.service';
import {NotificationStatusEnum} from 'libs/data-access/notification/src/lib/enums/notification-types.enum';
import {FacultyNotificationWithLink} from 'libs/data-access/notification/src/lib/models/request-response-notification.models';
import {NotificationService} from 'libs/data-access/notification/src/lib/services/notification.service';
import {NotificationContextService} from 'libs/data-access/notification/src/lib/services/notification-context.service';
import {NotificationTemplateComponent} from 'libs/faculty-performance/src/lib/components/performance-inbox/notification-space/notification-template/notification-template.component';
import {GlobalLoaderService} from 'libs/shared/component/src/lib/service/global-loader.service';
import {APP_URLS, AppUrls} from 'libs/shared/environment/src/lib/tokens/url.token';
import {IdentityService} from 'libs/shared/http/src/lib/services/identity/identity.service';
import {DialogContainerComponent} from 'libs/shared/ui/src/lib/components/dialog-container/dialog-container.component';
import {SelectOption} from 'libs/shared/ui/src/lib/models/select-option.model';
import {firstValueFrom} from 'libs/shared/util/src/lib/_helpers/rxjs.future';
import {
    InstitutionChangeModalComponent,
    InstitutionChangeModalData
} from '../sidebars/secondary/institution-change-modal/institution-change-modal.component';

@Component({
    imports: [CommonModule, NavigationListComponent, NotificationTemplateComponent, RouterLink],
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('collapsibleNav') collapsibleNavListInstance: ElementRef;

    collapsibleNav: Collapse;
    learnerText$: Observable<LearnerText>;
    accountInformation$: Observable<FacultyAccountModel>;
    institutionInformation$: Observable<InstitutionInformationModel>;
    accountName$: Observable<string>;
    changePortalOptionAvailable: boolean;
    canMimicLearner = false;
    institutionName = toSignal(
        this.institutionContextService.contextInstitutionInformation$.pipe(map((info) => info.InstitutionName))
    );
    institutionSelectData: Array<SelectOption> = [];
    selectedInstitution = signal<SelectOption | null>(null);
    hasMultipleInstitutions = computed(() => this.institutionSelectData.length > 1);
    unreadNotificationCount = signal(0);
    showNotificationPanel = signal(false);
    notificationService = inject(NotificationService);
    contextNotificationService = inject(NotificationContextService);
    notifications = signal<FacultyNotificationWithLink[]>([]);
    private readonly allNotificationCategoriesId = 0;
    private readonly newNotificationStatus = 1;
    readonly viewableNotificationCount = 5;

    showPerformancePage$: Observable<boolean> = this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith({urlAfterRedirects: this.router.url} as NavigationEnd),
        map((event: NavigationEnd) => event.urlAfterRedirects.includes('performance'))
    );

    private isApexInstitution = toSignal(
        this.institutionContextService.contextInstitutionInformation$.pipe(
            map((institution) => institution.IsApexInstitution)
        ),
        {initialValue: false}
    );

    needHelpUrl = computed(() =>
        this.isApexInstitution()
            ? 'https://support.truelearn.net/apex-faculty/'
            : 'https://support.truelearn.net/adminguide/'
    );

    blogUrl = computed(() =>
        this.isApexInstitution() ? 'https://www.apexanesthesia.com/blog/' : 'https://truelearn.com/resource-library/'
    );

    private unsubscriber = new Subject<void>();

    homePageLink = `${this.appUrls.CLASSIC_URL}/Institution/Home.aspx`;

    constructor(
        private appContextService: FacultyAppContextService,
        @Inject(APP_URLS) private appUrls: AppUrls,
        private identityService: IdentityService,
        private facultyService: FacultyService,
        private institutionContextService: InstitutionContextService,
        private institutionService: InstitutionService,
        private dialog: Dialog,
        private router: Router,
        private globalLoaderService: GlobalLoaderService,
        private destroy: DestroyRef,
        private viewContainerRef: ViewContainerRef
    ) {
        this.learnerText$ = this.institutionContextService.contextLearnerText$.pipe(
            startWith({SingularForm: 'Learner', PluralForm: 'Learners'})
        );
    }

    ngOnInit(): void {
        this.accountInformation$ = this.appContextService.contextAccountInformation$.pipe(
            filter((account) => !!account.Account.FirstName && !!account.Account.LastName),
            map((accountInformation) => accountInformation.Account)
        );

        this.accountInformation$
            .pipe(
                first((account) => !!account.ExternalId),
                takeUntil(this.unsubscriber),
                switchMap(() => this.facultyService.getMultiplePortalsAvailable())
            )
            .subscribe((isAvailible) => (this.changePortalOptionAvailable = isAvailible));

        this.accountName$ = this.accountInformation$.pipe(
            map(
                (accountInformation) =>
                    `${accountInformation.FirstName} ${accountInformation.LastName.substring(0, 1)}.`
            )
        );

        this.facultyService
            .getFacultyMemberAccountId()
            .pipe(
                tap((respose) => {
                    this.canMimicLearner = !!respose && respose.Completed && !!respose.FacultyMemberAccountId;
                }),
                takeUntil(this.unsubscriber)
            )
            .subscribe();

        this.institutionInformation$ = this.institutionContextService.contextInstitutionInformation$;

        // Subscribe to institution list for the inst. switcher
        combineLatest([
            this.institutionContextService.institutionsList$,
            this.institutionContextService.contextInstitutionInformation$
        ])
            .pipe(takeUntilDestroyed(this.destroy))
            .subscribe(([institutions, currentInstitution]) => {
                this.institutionSelectData = institutions.map((institution) => ({
                    id: institution.InstitutionId,
                    text: institution.InstitutionName
                }));

                const selected = this.institutionSelectData.find(
                    (item) => item.text === currentInstitution.InstitutionName
                );
                this.selectedInstitution.set(selected || null);
            });

        // Reload notifications when a notification status changes (deleted or read), so the read/deleted notification is removed from the list
        this.contextNotificationService.contextNotificationStatusChanged$
            .pipe(
                switchMap(() => this.getUnreadNotifications()),
                takeUntil(this.unsubscriber)
            )
            .subscribe((notifications) => {
                this.notifications.set(notifications);
                this.unreadNotificationCount.set(notifications.filter((x) => !x.IsRead).length);
            });

        // Initial load
        this.getUnreadNotifications()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((notifications) => {
                this.notifications.set(notifications);
                this.unreadNotificationCount.set(notifications.filter((x) => !x.IsRead).length);
            });
    }

    ngOnDestroy(): void {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }

    ngAfterViewInit(): void {
        this.collapsibleNav = new Collapse(this.collapsibleNavListInstance.nativeElement, {toggle: false});
    }

    toggleCollapse(): void {
        this.collapsibleNav.toggle();
    }

    async logout(): Promise<void> {
        await firstValueFrom(
            this.facultyService.logOut().pipe(
                finalize(() => {
                    this.identityService.bffLogout();
                })
            )
        );
    }

    navigateToLegacy(): void {
        location.href = `${this.appUrls.CLASSIC_URL}/Login.aspx?lo=1`;
    }

    async goToMemberInterface(): Promise<void> {
        if (!this.canMimicLearner) {
            return;
        }

        const institution = await firstValueFrom(this.institutionInformation$);
        const url = `${this.appUrls.AUTH_SERVER_URL}/api/Custom/MimicMemberInterface?institutionId=${
            institution.InstitutionId
        }`;
        window.open(url, '_blank');
    }

    async onChangePortalClick(): Promise<void> {
        this.identityService.redirectToSelectPortal();
    }

    onManageTagsClick(): void {
        this.router.navigate(['questions', 'tag-manager']);
    }

    // Close the notification panel when clicking outside of it
    @HostListener('document:click', ['$event'])
    onClickOutside(event: Event): void {
        const target = event.target as HTMLElement;

        if (!target.closest('.bell-wrapper')) {
            this.showNotificationPanel.set(false);
        }
    }

    // Toggle the notification panel on bell click
    toggleNotifications(): void {
        this.showNotificationPanel.set(!this.showNotificationPanel());
    }

    closeNotificationPanel(): void {
        this.showNotificationPanel.set(false);
    }

    getUnreadNotifications(): Observable<FacultyNotificationWithLink[]> {
        return this.notificationService
            .getFacultyNotifications({
                CategoryId: this.allNotificationCategoriesId,
                CurrentPage: 1,
                PageSize: 100,
                NotificationTypes: [1, 2, 3, 4, 5, 6],
                NotificationState: this.newNotificationStatus
            })
            .pipe(
                map((response) => {
                    if (!response.Completed) {
                        throw new Error();
                    }

                    return response.Notifications;
                }),
                catchError((error) => {
                    console.error('Error loading notifications:', error);
                    return of([]);
                })
            );
    }

    async markNotificationsAsRead(): Promise<void> {
        // Get the viewable notifications from the drop down that are unread
        const notificationsToRead = this.notifications()
            .slice(0, this.viewableNotificationCount)
            .filter((notification) => !notification.IsRead);
        if (notificationsToRead.length === 0) {
            return;
        }

        const result = await firstValueFrom(
            this.notificationService.changeNotificationsStatus(
                notificationsToRead.map((n) => n.Notification.Id),
                NotificationStatusEnum.Read
            )
        );

        if (result.Completed) {
            // We need to trigger the notification context to fetch a new set of unread notifications (just grabbing the first one is enough)
            this.contextNotificationService.setNotificationStatusChanged(
                notificationsToRead[0],
                NotificationStatusEnum.Read
            );
        }
    }

    onChangeInstitutionClick(): void {
        this.dialog
            .open<string, InstitutionChangeModalData>(InstitutionChangeModalComponent, {
                data: {
                    institutions: this.institutionSelectData,
                    selectedId: this.selectedInstitution()?.id || ''
                },
                panelClass: ['modal-md'],
                disableClose: true,
                ariaLabel: 'Change institution dialog',
                container: DialogContainerComponent
            })
            .closed.pipe(
                first(),
                filter(
                    (selectedInstitutionId) =>
                        selectedInstitutionId && selectedInstitutionId !== this.selectedInstitution()?.id
                ),
                tap(() => this.globalLoaderService.show(this.viewContainerRef)),
                switchMap((selectedInstitutionId) =>
                    this.institutionService.setFacultyInstitution(selectedInstitutionId)
                ),
                tap(() => {
                    let returnUrl = this.router.url.split('/').filter(Boolean);
                    const viewIndex = returnUrl.indexOf('view');
                    if (viewIndex !== -1) {
                        returnUrl = returnUrl.slice(0, viewIndex);
                    }
                    this.globalLoaderService.hide();
                    location.href = returnUrl.join('/');
                }),
                takeUntil(this.unsubscriber)
            )
            .subscribe();
    }
}
